import type {
  Attachment,
  DocumentReference,
  Organization,
  Practitioner,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes'
import { type DateString, formatDate } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import { invariant } from '@seen/core/invariant'
import { COMMUNICATION_MEDIUMS } from '@seen/core/models/communications/constants'
import { ExtendedCommunication } from '@seen/core/models/communications/ExtendedCommunication'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { OrganizationType } from '@seen/core/models/organizations/constants'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { isValidFaxNumber, phoneSearchVariants } from '@seen/core/phone'
import {
  getReferenceString,
  referenceFromReferenceString,
} from '@seen/core/references'
import { isOfType } from '@seen/core/utils'

import { Button, cn, ImageZoom, Label, Separator } from '@seen/ui'
import { IconFileText, IconPlus, IconZoomIn } from '@tabler/icons-react'
import { type JSX, lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { toast } from 'sonner'
import { z } from 'zod'
import { AttachmentPDFModal } from '#src/components/attachments/AttachmentPDFModal.tsx'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { ModalStackLayers } from '#src/components/core/dialog/ModalStack.tsx'
import {
  ModalStackContext,
  type ModalStackLayer,
  useModalStackState,
} from '#src/components/core/dialog/ModalStackContext.ts'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Textarea } from '#src/components/core/Textarea.tsx'
import { DocumentPickerDialog } from '#src/components/document-picker/DocumentPickerDialog.tsx'
import type { DocumentPickerDocument } from '#src/components/document-picker/DocumentPickerView.tsx'
import { OrganizationNameplate } from '#src/components/nameplates/OrganizationNameplate.tsx'
import { ParticipantNameplate } from '#src/components/nameplates/ParticipantNameplate.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { UserPlaceholderNameplate } from '#src/components/nameplates/UserPlaceholderNameplate.tsx'
import { OrganizationOption } from '#src/components/options/OrganizationOption.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { UserPlaceholderOption } from '#src/components/options/UserPlaceholderOption.tsx'
import { NetworkStatusBadge } from '#src/components/practitioner/NetworkStatusBadge.tsx'
import { useSeenApi } from '#src/contexts/index.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { useExportPdf } from '#src/hooks/useExportPdf.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useLabCopySecondaryFilter } from '#src/hooks/useLabCopySecondaryFilter.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useFaxRecipientOrganizations } from '#src/hooks/useOrganizations.ts'
import { useParticipants } from '#src/hooks/useParticipants.ts'
import { useFaxRecipientPractitioners } from '#src/hooks/usePractitioners.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useStableAttachmentUrl } from '#src/hooks/useStableAttachmentUrl.ts'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'
import { renderPdfBlobPreviewDataUrl } from '#src/utils/printPdfBlob.ts'

// The two create-recipient forms are the last thing pinning the NPI registry
// form tree into the portal's initial chunk, yet they are only reachable three
// cold clicks deep: open the fax modal, "Create recipient", then pick a type.
// `PractitionerCreateForm` itself imports `OrganizationCreateForm`, so the two
// travel together into one on-demand chunk.
const OrganizationCreateForm = lazy(() =>
  import('#src/components/organization/OrganizationCreateForm.tsx').then(
    (m) => ({ default: m.OrganizationCreateForm })
  )
)
const PractitionerCreateForm = lazy(() =>
  import('#src/components/practitioner/PractitionerCreateForm.tsx').then(
    (m) => ({ default: m.PractitionerCreateForm })
  )
)

// This modal renders from `Header.tsx`, outside the router's `<Outlet />`
// ErrorBoundary, so a chunk fetch that fails (a deploy rotates the hash under
// an open tab) would otherwise reach the root and blank the whole app.
function CreateRecipientLayer({ children }: { children: JSX.Element }) {
  return (
    <ErrorBoundary
      // `lazy` caches a rejected import, so remounting this layer — including
      // closing and reopening the modal — replays the same failure. Only a
      // reload gets a fresh module registry, so that's what we tell the user.
      fallback={
        <div className="p-4 text-sm text-muted-foreground">
          This form failed to load. Reload the page to try again.
        </div>
      }
    >
      <Suspense fallback={<Loader />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

function FaxPdfThumbnail({
  url,
  title,
  previewBlob,
}: {
  url: string
  title: string
  previewBlob?: Blob
}): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false)
  const [rasterPreviewUrl, setRasterPreviewUrl] = useState<string | null>(null)
  const thumbnailClass =
    'relative overflow-hidden rounded border bg-zinc-100 dark:bg-zinc-900 size-24 shrink-0'

  useEffect(() => {
    if (!previewBlob) {
      setRasterPreviewUrl(null)
      return
    }
    let cancelled = false
    void renderPdfBlobPreviewDataUrl(previewBlob).then((dataUrl) => {
      if (!cancelled) {
        setRasterPreviewUrl(dataUrl)
      }
    })
    return () => {
      cancelled = true
    }
  }, [previewBlob])

  return (
    <>
      <button
        type="button"
        onClick={() => setIsExpanded(true)}
        className={cn(thumbnailClass, 'group cursor-zoom-in')}
        aria-label={`Expand ${title}`}
      >
        {rasterPreviewUrl ? (
          <img
            src={rasterPreviewUrl}
            alt=""
            className="size-full object-cover object-top"
          />
        ) : (
          <iframe
            src={`${url}#view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
            title={title}
            // The iframe must not swallow the button's click.
            className="pointer-events-none absolute -top-2 h-[200%] w-[200%] origin-top-left scale-50 border-0"
          />
        )}
        <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
          <IconZoomIn className="size-5 text-white" />
        </span>
      </button>
      <AttachmentPDFModal
        url={url}
        title={title}
        open={isExpanded}
        onOpenChange={setIsExpanded}
      />
    </>
  )
}

// Fixed-size preview thumbnail of a saved document's first attachment, clipped
// to the thumbnail box and expandable to full size.
function SelectedFaxDocumentThumbnail({
  attachment,
  title,
}: {
  attachment: Attachment | undefined
  title: string
}): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false)
  const stableUrl = useStableAttachmentUrl(attachment?.url)
  const contentType = attachment?.contentType

  const thumbnailClass =
    'relative overflow-hidden rounded border bg-zinc-100 dark:bg-zinc-900 size-24 shrink-0'

  if (stableUrl && contentType === 'application/pdf') {
    return <FaxPdfThumbnail url={stableUrl} title={title} />
  }

  if (stableUrl && contentType?.startsWith('image/')) {
    return (
      <div className={thumbnailClass}>
        <ImageZoom isZoomed={isExpanded} onZoomChange={setIsExpanded}>
          <img
            src={stableUrl}
            alt={title}
            className="size-24 cursor-zoom-in object-cover"
          />
        </ImageZoom>
      </div>
    )
  }

  return (
    <div className={cn(thumbnailClass, 'flex items-center justify-center')}>
      <IconFileText className="size-8 text-muted-foreground" />
    </div>
  )
}

export interface FaxPendingDocument {
  readonly id: string
  readonly title: string
  readonly filename: string
  readonly blob: Blob
  readonly date?: DateString
}

function PendingFaxDocument({
  document,
  onRemove,
}: {
  document: FaxPendingDocument
  onRemove: () => void
}): JSX.Element {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    const url = URL.createObjectURL(document.blob)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [document.blob])

  return (
    <div className="flex items-start gap-3 p-3">
      {previewUrl ? (
        <FaxPdfThumbnail
          url={previewUrl}
          title={document.title}
          previewBlob={document.blob}
        />
      ) : (
        <div className="relative flex size-24 shrink-0 items-center justify-center overflow-hidden rounded border bg-zinc-100 dark:bg-zinc-900">
          <IconFileText className="size-8 text-muted-foreground" />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="truncate text-sm font-medium">{document.title}</div>
        {document.date && (
          <div className="text-xs text-muted-foreground">
            {formatDate(document.date)}
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="shrink-0"
      >
        Remove
      </Button>
    </div>
  )
}

function SelectedFaxDocument({
  document,
  onRemove,
}: {
  document: ExtendedDocumentReference
  onRemove: () => void
}): JSX.Element {
  return (
    <div className="flex items-start gap-3 p-3">
      <SelectedFaxDocumentThumbnail
        attachment={document.attachments[0]}
        title={document.title || 'Untitled Document'}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="truncate text-sm font-medium">
          {document.title || 'Untitled Document'}
        </div>
        {document.date && (
          <div className="text-xs text-muted-foreground">
            {formatDate(document.date)}
          </div>
        )}
        {document.attachments.length > 1 && (
          <div className="text-xs text-muted-foreground">
            +{document.attachments.length - 1} more attachments
          </div>
        )}
        {document.description && (
          <div className="line-clamp-1 text-xs text-muted-foreground">
            {document.description}
          </div>
        )}
        {document.aiSummary && (
          <div className="line-clamp-2 text-xs text-muted-foreground">
            {document.aiSummary}
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="shrink-0"
      >
        Remove
      </Button>
    </div>
  )
}

const sendFaxFormSchema = z
  .object({
    recipientRef: z
      .string({ required_error: 'Recipient is required' })
      .nullable(),
    participantRef: z
      .string({ required_error: 'Participant is required' })
      .nullable(),
    selectedDocuments: z.array(z.string()),
    pendingDocumentIds: z.array(z.string()),
    includeFacesheet: z.boolean(),
    message: z.string().nullable(),
    // The fax number for the selected (recipient, number) row; set by the
    // recipient combobox, not user-typed.
    destinationFaxNumber: z.string().nullable(),
  })
  .refine(
    (data) => {
      const hasDocuments =
        data.selectedDocuments.length > 0 || data.pendingDocumentIds.length > 0
      const hasFacesheet = data.includeFacesheet
      const hasMessage = Boolean(data.message?.trim())

      return hasDocuments || hasFacesheet || hasMessage
    },
    {
      message:
        'Please select at least one document, include facesheet, or enter a message',
      path: ['selectedDocuments'],
    }
  )
  .refine(
    (data) =>
      !data.destinationFaxNumber || isValidFaxNumber(data.destinationFaxNumber),
    {
      // The number comes from the picked recipient, not a typed field, so the
      // message names the recipient rather than telling the user to "enter" one.
      message: "The selected recipient's fax number isn't a valid number",
      path: ['destinationFaxNumber'],
    }
  )

type SendFaxFormValues = z.infer<typeof sendFaxFormSchema>

// Recipient combobox rows are keyed by (recipient, fax number) pair — a
// provider can have more than one fax number on file — so the option value
// encodes both, joined on a separator that can't appear in either half.
const RECIPIENT_OPTION_SEPARATOR = '|'

function recipientOptionValue(reference: string, faxNumber: string): string {
  return `${reference}${RECIPIENT_OPTION_SEPARATOR}${faxNumber}`
}

interface ParseRecipientOptionValueResult {
  reference: string
  faxNumber: string
}

function parseRecipientOptionValue(
  value: string
): ParseRecipientOptionValueResult {
  const separatorIndex = value.indexOf(RECIPIENT_OPTION_SEPARATOR)
  return {
    reference: value.slice(0, separatorIndex),
    faxNumber: value.slice(separatorIndex + 1),
  }
}

export interface SendFaxModalProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /**
   * When provided, opens the modal pre-seeded with this document selected and
   * its participant set as the recipient context — used to launch Send Fax
   * directly from a document detail view. Defaults to an empty picker.
   */
  readonly initialDocument?: ExtendedDocumentReference
  // Participant to select when the modal is opened without a chart document,
  // such as a generated medications PDF.
  readonly initialParticipantRef?: string
  // A generated file that is not yet a DocumentReference. Shown in Selected
  // Documents and uploaded as a fax attachment on send.
  readonly initialPendingDocument?: FaxPendingDocument
  readonly isPreparingInitialDocument?: boolean
}

// The create-recipient wizard occupies the modal content while active (as modal
// stack layers); the send-fax form stays mounted (hidden) so its state survives
// the round trip. Back/breadcrumb navigation is owned by the stack header.
function RecipientTypeStep({
  onPickOrganization,
  onPickPractitioner,
  onBack,
}: {
  onPickOrganization: () => void
  onPickPractitioner: () => void
  onBack: () => void
}): JSX.Element {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Who are you faxing?</p>
      <div className="space-y-2">
        <Button
          type="button"
          variant="outline"
          className="h-auto w-full justify-start py-3"
          onClick={onPickOrganization}
        >
          <span className="flex flex-col items-start text-left">
            <span className="font-medium">Organization or facility</span>
            <span className="text-xs font-normal text-muted-foreground">
              Clinic, lab, imaging center, office, or records department
            </span>
          </span>
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-auto w-full justify-start py-3"
          onClick={onPickPractitioner}
        >
          <span className="flex flex-col items-start text-left">
            <span className="font-medium">Individual clinician</span>
            <span className="text-xs font-normal text-muted-foreground">
              A specific provider, linked to their organization
            </span>
          </span>
        </Button>
      </div>
      {/* Footer action bottom-right, matching the create forms' Cancel/Submit
          convention (the header carries only the breadcrumb, no Back button). */}
      <div className="flex justify-end pt-2">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
      </div>
    </div>
  )
}

export function SendFaxModal({
  open,
  onOpenChange,
  initialDocument,
  initialParticipantRef,
  initialPendingDocument,
  isPreparingInitialDocument = false,
}: SendFaxModalProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const { seenApi, npi } = useSeenApi()
  const { exportPdf, viaPortal } = useExportPdf()
  const medplum = useMedplum()
  const currentPractitioner = useCurrentPractitioner()
  const participants = useParticipants()
  const searchedPractitioners = useFaxRecipientPractitioners()
  const searchedOrganizations = useFaxRecipientOrganizations()
  const currentParticipant = useTaskComposerStore((s) => s.currentParticipant)
  const currentParticipantRefString = currentParticipant
    ? getReferenceString(currentParticipant)
    : null

  const initialParticipantRefString =
    initialDocument?.subject?.reference ?? initialParticipantRef ?? null

  const [pendingDocuments, setPendingDocuments] = useState<
    FaxPendingDocument[]
  >(() => (initialPendingDocument ? [initialPendingDocument] : []))

  const [isGeneratingFacesheet, setIsGeneratingFacesheet] = useState(false)
  const [showDocumentPicker, setShowDocumentPicker] = useState(false)

  // The nested create-recipient wizard runs as a modal stack: the type chooser,
  // the practitioner form, and (under the practitioner form) the organization
  // form each become a layer with its own title/subtitle and breadcrumb.
  const { layers, handle: stack } = useModalStackState()
  // A recipient created through the wizard, spliced into the option lists until
  // the invalidated recipient searches refetch and include it.
  const [createdRecipient, setCreatedRecipient] = useState<
    ExtendedPractitioner | ExtendedOrganization | null
  >(null)

  const createCommunication = useCreateMutation<ExtendedCommunication>()

  const practitioners = useMemo(() => {
    const created =
      createdRecipient instanceof ExtendedPractitioner ? createdRecipient : null
    if (
      !created ||
      searchedPractitioners.some(
        (p) => p.reference.reference === created.reference.reference
      )
    ) {
      return searchedPractitioners
    }
    return [created, ...searchedPractitioners]
  }, [searchedPractitioners, createdRecipient])

  const organizations = useMemo(() => {
    const created =
      createdRecipient instanceof ExtendedOrganization ? createdRecipient : null
    if (
      !created ||
      searchedOrganizations.some(
        (o) => o.reference.reference === created.reference.reference
      )
    ) {
      return searchedOrganizations
    }
    return [created, ...searchedOrganizations]
  }, [searchedOrganizations, createdRecipient])

  const form = useForm({
    schema: sendFaxFormSchema,
    defaultValues: {
      recipientRef: null,
      participantRef:
        initialParticipantRefString ?? currentParticipant?.reference ?? null,
      selectedDocuments: initialDocument ? [initialDocument.id] : [],
      pendingDocumentIds: initialPendingDocument
        ? [initialPendingDocument.id]
        : [],
      includeFacesheet: false,
      message: null,
      destinationFaxNumber: null,
    },
  })
  const { setValue } = form

  // Re-seed the form when a new document is supplied (the modal can stay
  // mounted across opens). Takes precedence over the task-composer participant.
  const initialDocumentChanged = useDidChange(initialDocument?.id)
  if (initialDocumentChanged && initialDocument) {
    form.setValue('participantRef', initialParticipantRefString)
    form.setValue('selectedDocuments', [initialDocument.id])
  }

  useEffect(() => {
    if (!initialPendingDocument) {
      return
    }
    setPendingDocuments([initialPendingDocument])
    setValue('pendingDocumentIds', [initialPendingDocument.id])
  }, [initialPendingDocument, setValue])

  const participantChanged = useDidChange(currentParticipantRefString)
  if (
    participantChanged &&
    currentParticipantRefString &&
    !initialDocument &&
    !initialParticipantRef
  ) {
    form.setValue('participantRef', currentParticipantRefString)
  }

  const opened = useDidChange(open)
  if (opened && open) {
    if (!initialDocument && initialParticipantRef) {
      form.setValue('participantRef', initialParticipantRef)
    }
  }

  const participantRef = form.watch('participantRef')
  const recipientRefString = form.watch('recipientRef')
  const destinationFaxNumber = form.watch('destinationFaxNumber')
  const selectedDocuments = form.watch('selectedDocuments')

  const recipientRef = useMemo(
    () =>
      recipientRefString
        ? referenceFromReferenceString<Practitioner | Organization>(
            recipientRefString
          )
        : undefined,
    [recipientRefString]
  )

  // The selected recipient's model instance, used to render its network-status
  // indicator (both Practitioner and Organization expose `networkStatus`).
  const selectedRecipient = useMemo(
    () =>
      [...practitioners, ...organizations].find(
        (r) => r.reference.reference === recipientRefString
      ),
    [recipientRefString, practitioners, organizations]
  )

  const withoutLabCopySecondaries = useLabCopySecondaryFilter()
  const { data: availableDocuments = [], isLoading: isLoadingDocuments } =
    useSearch(
      ExtendedDocumentReference,
      participantRef
        ? withoutLabCopySecondaries({
            subject: participantRef,
            _sort: '-date',
            _count: 500,
          })
        : undefined,
      { query: { enabled: !!participantRef } }
    )

  // The participant search may not yet include `initialDocument` (still
  // loading, or paged out), so merge it in — deduped by id — to keep the
  // pre-seeded selection renderable and submittable.
  const documentPool = useMemo(() => {
    if (!initialDocument) {
      return availableDocuments
    }
    if (availableDocuments.some((d) => d.id === initialDocument.id)) {
      return availableDocuments
    }
    return [initialDocument, ...availableDocuments]
  }, [availableDocuments, initialDocument])

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) {
      // Drop the wizard-created recipient so it doesn't linger in the options
      // list on reopen; the invalidated searches supply it going forward.
      setCreatedRecipient(null)
      stack.clear()
    }
    onOpenChange(nextOpen)
  }

  const handleClose = (): void => {
    setPendingDocuments([])
    form.reset()
    handleOpenChange(false)
  }

  // A recipient created (or matched) by the wizard becomes the selection: its
  // first fax number is the destination, and the send form returns to view.
  const handleRecipientCreated = (
    recipient: ExtendedPractitioner | ExtendedOrganization
  ): void => {
    setCreatedRecipient(recipient)
    form.setValue('recipientRef', recipient.reference.reference)
    form.setValue('destinationFaxNumber', recipient.faxNumbers()[0] ?? null)
    stack.clear()
  }

  // The "open instead" path selects a pre-existing Seen org matched by NPI.
  // Unlike the create path (which enforces `requireFax`), such an org may
  // predate the fax requirement and carry no fax number. Selecting it would
  // strand the send form with a fax-less recipient it can't fix, so keep the
  // wizard open and surface why instead.
  const handleOpenExisting = (
    recipient: ExtendedPractitioner | ExtendedOrganization
  ): void => {
    if (recipient.faxNumbers().length === 0) {
      toast.error('That recipient has no fax number on file.')
      return
    }
    handleRecipientCreated(recipient)
  }

  // Layer builders for the create-recipient wizard. Each closes over the
  // recipient-combobox search text captured when "Create recipient" was clicked,
  // seeding the layer's NPI registry lookup. `npi` is guaranteed here — the
  // create affordance is only offered when it's available.
  const organizationLayer = (search: string): ModalStackLayer => ({
    key: 'create-organization',
    breadcrumb: 'Add organization',
    title: 'Add new organization',
    subtitle: 'Add an organization or facility to fax.',
    render: () =>
      npi ? (
        <CreateRecipientLayer>
          <OrganizationCreateForm
            type={OrganizationType.EXTERNAL_PROVIDER}
            searchNpiOrganizations={npi.searchOrganizations}
            allowWithoutNpi
            requireFax
            initialNpiSearch={search}
            onCreated={handleRecipientCreated}
            // The entered NPI already belongs to a Seen org — select it as the
            // recipient instead of creating a duplicate, unless it has no fax
            // number (see handleOpenExisting).
            onOpenExisting={handleOpenExisting}
            onCancel={stack.pop}
          />
        </CreateRecipientLayer>
      ) : null,
  })

  const practitionerLayer = (search: string): ModalStackLayer => ({
    key: 'create-practitioner',
    breadcrumb: 'Add contact',
    title: 'Add new contact',
    subtitle: 'Add a clinician to fax.',
    render: () =>
      npi ? (
        <CreateRecipientLayer>
          <PractitionerCreateForm
            searchNpi={npi.searchIndividuals}
            getAffiliations={npi.getAffiliations}
            searchNpiOrganizations={npi.searchOrganizations}
            requireFax
            initialNpiSearch={search}
            onCreated={handleRecipientCreated}
            onCancel={stack.pop}
          />
        </CreateRecipientLayer>
      ) : null,
  })

  const recipientTypeLayer = (search: string): ModalStackLayer => ({
    key: 'recipient-type',
    breadcrumb: 'New recipient',
    title: 'Create recipient',
    subtitle: "Choose whether you're adding a clinician or an organization.",
    // Picking a type replaces this chooser rather than stacking on it, so the
    // breadcrumb reads "Send fax › Add contact", not "…› New recipient › …".
    render: () => (
      <RecipientTypeStep
        onPickOrganization={() => stack.replace(organizationLayer(search))}
        onPickPractitioner={() => stack.replace(practitionerLayer(search))}
        onBack={stack.pop}
      />
    ),
  })

  const handleSubmit = form.handleSubmit(
    async (values: SendFaxFormValues): Promise<void> => {
      invariant(values.participantRef, 'Participant is required')
      invariant(values.recipientRef, 'Recipient is required')

      try {
        let facesheetAttachment: Attachment | undefined

        if (values.includeFacesheet) {
          setIsGeneratingFacesheet(true)
          const participantId = values.participantRef.split('/')[1]
          invariant(participantId, 'Invalid participant reference')

          // The Lambda path keeps the text body it always
          // attached; the portal returns the PDF as a Blob.
          const data = viaPortal
            ? (await exportPdf({ kind: 'facesheet', participantId })).blob
            : (await seenApi.post<string>(`/pdf/facesheet/${participantId}`))
                .data
          facesheetAttachment = await medplum.createAttachment({
            data,
            contentType: 'application/pdf',
            filename: `facesheet_${participantId}_${Date.now()}.pdf`,
          })

          setIsGeneratingFacesheet(false)
        }

        const communication = new ExtendedCommunication({
          status: 'preparation',
          subject: referenceFromReferenceString(values.participantRef),
          sender: currentPractitioner.reference,
          recipient: [referenceFromReferenceString(values.recipientRef)],
          medium: [COMMUNICATION_MEDIUMS.FAXWRIT],
        })

        const relatedOrders = new Set<Reference<ServiceRequest>>()
        const relatedDocuments = new Set<Reference<DocumentReference>>()

        for (const docId of values.selectedDocuments) {
          const doc = documentPool.find((d) => d.id === docId)
          if (doc) {
            communication.addDocumentPayload(doc.reference)

            const serviceRequestRefs = (doc.related ?? []).filter((r) =>
              isOfType(r, 'ServiceRequest')
            )
            for (const ref of serviceRequestRefs) {
              relatedOrders.add(ref)
            }
            relatedDocuments.add(doc.reference)
          }
        }

        const basedOn = [
          ...Array.from(relatedOrders),
          ...Array.from(relatedDocuments),
        ]

        if (basedOn.length > 0) {
          communication.basedOn = basedOn
        }

        for (const pending of pendingDocuments) {
          if (!values.pendingDocumentIds.includes(pending.id)) {
            continue
          }
          setIsGeneratingFacesheet(true)
          const attachment = await medplum.createAttachment({
            data: pending.blob,
            contentType: 'application/pdf',
            filename: pending.filename,
          })
          communication.addAttachmentPayload(attachment)
          setIsGeneratingFacesheet(false)
        }

        if (facesheetAttachment) {
          communication.addAttachmentPayload(facesheetAttachment)
        }

        if (values.message) {
          communication.addTextPayload(values.message)
        }

        if (values.destinationFaxNumber) {
          communication.destinationFaxNumber = values.destinationFaxNumber
        }

        await createCommunication.mutateAsync(communication)

        toast.success('Fax queued for sending')
        handleClose()
      } catch (error) {
        toast.error('Failed to send fax')
        console.error(error)
        setIsGeneratingFacesheet(false)
      }
    }
  )

  // The picker opens pre-seeded with the current selection, so its returned set
  // is authoritative: checking adds, unchecking removes.
  const insertDocuments = (docIds: string[]): void => {
    setShowDocumentPicker(false)
    form.setValue('selectedDocuments', docIds)
  }

  const removeDocument = (docId: string): void => {
    form.setValue(
      'selectedDocuments',
      selectedDocuments.filter((id) => id !== docId)
    )
  }

  const removePendingDocument = (docId: string): void => {
    setPendingDocuments((current) => current.filter((doc) => doc.id !== docId))
    form.setValue(
      'pendingDocumentIds',
      form.getValues('pendingDocumentIds').filter((id) => id !== docId)
    )
  }

  const participantOptions = [
    {
      value: null,
      label: (
        <UserPlaceholderOption isSelected={!participantRef}>
          No participant
        </UserPlaceholderOption>
      ),
      keywords: ['No participant'],
    },
    ...participants.map((p) => ({
      value: p.reference.reference,
      label: (
        <ParticipantOption
          participant={p.reference}
          isSelected={p.reference.reference === participantRef}
        />
      ),
      keywords: p.searchKeywords,
    })),
  ]

  const selectedRecipientOptionValue =
    recipientRefString && destinationFaxNumber
      ? recipientOptionValue(recipientRefString, destinationFaxNumber)
      : null

  // No null "clear" row: a recipient is required to send, so the way to change
  // a selection is picking another row.
  const recipientOptions = [
    ...practitioners.flatMap((p) =>
      p.faxNumbers().map((faxNumber) => {
        const value = recipientOptionValue(p.reference.reference, faxNumber)
        return {
          value,
          label: (
            <PractitionerOption
              practitioner={p.reference}
              faxNumber={faxNumber}
              isSelected={value === selectedRecipientOptionValue}
              isPreferred={p.isPreferred}
            />
          ),
          keywords: [
            ...p.searchKeywords,
            ...phoneSearchVariants(faxNumber).split(','),
          ],
          // Same scale as org availabilityRank so a preferred practitioner and a
          // preferred org share the 'preferred' tier in this mixed list.
          boost: p.isPreferred ? 2 : 0,
        }
      })
    ),
    ...organizations.flatMap((org) =>
      org.faxNumbers().map((faxNumber) => {
        const value = recipientOptionValue(org.reference.reference, faxNumber)
        return {
          value,
          label: (
            <OrganizationOption
              organization={org.reference}
              faxNumber={faxNumber}
              isSelected={value === selectedRecipientOptionValue}
              availability={org.availability}
            />
          ),
          keywords: [
            ...org.searchKeywords,
            ...phoneSearchVariants(faxNumber).split(','),
          ],
          boost: org.availabilityRank,
        }
      })
    ),
  ]

  // Only faxable documents (PDF or image attachments) are offered in the picker.
  const pickerDocuments = useMemo<DocumentPickerDocument[]>(
    () =>
      documentPool
        .filter((doc) =>
          doc.attachments.some(
            (a) =>
              a.contentType === 'application/pdf' ||
              a.contentType?.startsWith('image/')
          )
        )
        .map((doc) => ({
          id: doc.id,
          title: doc.title || 'Untitled Document',
          date: doc.dateStringWithFallback ?? '',
          categories: doc.categories,
          attachments: doc.attachments,
          isSigned: doc.isConfirmed,
          subtitle: doc.description || doc.aiSummary || null,
          description: doc.description || null,
          aiSummary: doc.aiSummary || null,
          author: referenceDisplay(doc.author),
          transcription: doc.transcription ?? null,
        })),
    [documentPool]
  )

  const selectedDocumentsList = selectedDocuments
    .map((id) => documentPool.find((d) => d.id === id))
    .filter((doc): doc is ExtendedDocumentReference => doc !== undefined)

  const isSubmitting = createCommunication.isPending || isGeneratingFacesheet

  return (
    <ModalStackContext.Provider value={stack}>
      {/* The base send-fax dialog. It stays mounted under the wizard layers so
          recipient, documents, and message state survive the round trip, and
          shows through (dimmed) behind them. */}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogDisplay>
          <DialogHeader className="py-4 text-lg">
            <DialogTitle>Send Fax</DialogTitle>
          </DialogHeader>
          <DialogContent className="space-y-4 pt-0">
            <Form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Hidden fields so their schema-refine errors reach the
                      FormField error-toast path — neither has a visible input
                      (selectedDocuments is a cross-field rule; destinationFaxNumber
                      is set by the recipient picker, not typed). Without these,
                      a failed refine blocks submit silently. */}
                <FormField
                  control={form.control}
                  name="selectedDocuments"
                  render={() => null}
                />
                <FormField
                  control={form.control}
                  name="destinationFaxNumber"
                  render={() => null}
                />
                <FormField
                  control={form.control}
                  name="recipientRef"
                  render={({ field }) => (
                    <div className="space-y-2">
                      <Label>Recipient</Label>
                      <Combobox
                        options={recipientOptions}
                        onSelect={(value) => {
                          if (!value) {
                            field.onChange(null)
                            form.setValue('destinationFaxNumber', null)
                            return
                          }
                          const { reference, faxNumber } =
                            parseRecipientOptionValue(value)
                          field.onChange(reference)
                          form.setValue('destinationFaxNumber', faxNumber)
                        }}
                        createLabel={
                          npi && !isReadOnly
                            ? () => (
                                <span className="flex items-center gap-2">
                                  <IconPlus className="size-4" />
                                  Create recipient
                                </span>
                              )
                            : undefined
                        }
                        onCreate={(search) =>
                          stack.push(recipientTypeLayer(search))
                        }
                        alwaysShowCreate
                        stickyCreate
                        // Stricter than the 0.6 default: this list has
                        // thousands of (provider, fax) rows, and loose matching
                        // buries the create row under noise for junk queries.
                        fuseThreshold={0.3}
                        className="w-96"
                      >
                        <Button
                          variant="outline"
                          className="w-full justify-start gap-2 shadow-sm"
                        >
                          {isOfType(recipientRef, 'Practitioner') && (
                            <PractitionerNameplate
                              practitioner={recipientRef}
                              faxNumber={destinationFaxNumber ?? undefined}
                            />
                          )}
                          {isOfType(recipientRef, 'Organization') && (
                            <OrganizationNameplate
                              organization={recipientRef}
                              faxNumber={destinationFaxNumber ?? undefined}
                            />
                          )}
                          {!recipientRefString && (
                            <UserPlaceholderNameplate>
                              Select practitioner or organization
                            </UserPlaceholderNameplate>
                          )}
                        </Button>
                      </Combobox>
                      {selectedRecipient && (
                        <NetworkStatusBadge
                          status={selectedRecipient.networkStatus}
                        />
                      )}
                    </div>
                  )}
                />

                <FormField
                  control={form.control}
                  name="participantRef"
                  render={({ field }) => (
                    <div className="space-y-2">
                      <Label>Participant</Label>
                      <Combobox
                        options={participantOptions}
                        onSelect={field.onChange}
                      >
                        <Button
                          variant="outline"
                          className="w-full justify-start gap-2 shadow-sm"
                        >
                          {participantRef && (
                            <ParticipantNameplate
                              participant={participantRef}
                            />
                          )}
                          {!participantRef && (
                            <UserPlaceholderNameplate>
                              Select participant
                            </UserPlaceholderNameplate>
                          )}
                        </Button>
                      </Combobox>
                    </div>
                  )}
                />

                <Separator />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <div className="space-y-2">
                      <Label>Message (optional)</Label>
                      <Textarea
                        {...field}
                        value={field.value ?? ''}
                        onChange={(value) => field.onChange(value || null)}
                        className="w-full"
                        placeholder="Enter message"
                      />
                    </div>
                  )}
                />

                {participantRef && (
                  <FormField
                    control={form.control}
                    name="includeFacesheet"
                    render={({ field: { value, onChange, ...rest } }) => (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            {...rest}
                            value={value}
                            onChange={onChange}
                          />
                          <Label htmlFor={rest.id} className="cursor-pointer">
                            Include Facesheet (auto-generated)
                          </Label>
                        </div>
                      </div>
                    )}
                  />
                )}

                {(documentPool.length > 0 ||
                  pendingDocuments.length > 0 ||
                  isPreparingInitialDocument) && (
                  <>
                    {documentPool.length > 0 && (
                      <div className="space-y-2">
                        <Label>Add Documents</Label>
                        {isLoadingDocuments && <Loader />}
                        {!isLoadingDocuments && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowDocumentPicker(true)}
                            className="w-full justify-start gap-2 shadow-sm"
                          >
                            <UserPlaceholderNameplate>
                              Browse and select documents...
                            </UserPlaceholderNameplate>
                          </Button>
                        )}
                      </div>
                    )}

                    {(isPreparingInitialDocument ||
                      pendingDocuments.length > 0 ||
                      selectedDocumentsList.length > 0) && (
                      <div className="space-y-2">
                        <Label>
                          Selected Documents (
                          {pendingDocuments.length +
                            selectedDocumentsList.length +
                            (isPreparingInitialDocument ? 1 : 0)}
                          )
                        </Label>
                        <div className="max-h-64 overflow-y-auto rounded-md border">
                          {isPreparingInitialDocument && (
                            <div className="flex h-24 items-center gap-3 p-3">
                              <Loader />
                              <span className="text-sm text-muted-foreground">
                                Preparing medications PDF…
                              </span>
                            </div>
                          )}
                          {pendingDocuments.map((doc, index) => (
                            <div
                              key={doc.id}
                              className={cn(
                                (isPreparingInitialDocument ||
                                  index < pendingDocuments.length - 1 ||
                                  selectedDocumentsList.length > 0) &&
                                  'border-b'
                              )}
                            >
                              <PendingFaxDocument
                                document={doc}
                                onRemove={() => removePendingDocument(doc.id)}
                              />
                            </div>
                          ))}
                          {selectedDocumentsList.map((doc, index) => (
                            <div
                              key={doc.id}
                              className={cn(
                                index < selectedDocumentsList.length - 1 &&
                                  'border-b'
                              )}
                            >
                              <SelectedFaxDocument
                                document={doc}
                                onRemove={() => removeDocument(doc.id)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting || isReadOnly}>
                    {isSubmitting ? 'Sending...' : 'Send Fax'}
                  </Button>
                </div>
              </div>
            </Form>
          </DialogContent>
        </DialogDisplay>
      </Dialog>

      {/* The create-recipient wizard: each step is a dialog stacked over the
          base (and each other), with the parent layers visible behind it. */}
      <ModalStackLayers layers={layers} handle={stack} rootLabel="Send fax" />

      <DocumentPickerDialog
        open={showDocumentPicker}
        onOpenChange={setShowDocumentPicker}
        documents={pickerDocuments}
        initialSelectedIds={selectedDocuments}
        onInsert={insertDocuments}
      />
    </ModalStackContext.Provider>
  )
}
